import React, { useEffect, useState } from "react";

// The restaurant's AI provider, model and key for chart summaries. Stored encrypted on the
// auth service via the Data API; the saved key never comes back, only its last four characters.
const AI_SETTINGS_URL = "https://data.tablemate.net/ai-settings";

export default function AiSettingsCard({ accessToken, restaurantId }) {
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const apply = (data) => {
    setSettings(data);
    setProvider(data.provider || "");
    setModel(data.model || "");
    setBaseUrl(data.base_url || "");
    setApiKey("");
  };

  useEffect(() => {
    if (!accessToken || !restaurantId) return;
    let cancelled = false;
    setSettings(null);
    setError(null);
    setSuccess(null);
    fetch(`${AI_SETTINGS_URL}?rid=${encodeURIComponent(restaurantId)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
        if (!cancelled) apply(data);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [accessToken, restaurantId]);

  const providers = settings?.providers || {};
  const info = providers[provider];
  const keyKept =
    settings &&
    provider !== "" &&
    provider === settings.provider &&
    settings.has_key &&
    (!info?.base_url || baseUrl.replace(/\/+$/, "") === (settings.base_url || ""));

  const changeProvider = (value) => {
    setProvider(value);
    setModel(value === settings.provider ? settings.model || "" : providers[value]?.models?.[0] || "");
    setBaseUrl(value === settings.provider ? settings.base_url || "" : providers[value]?.default_base_url || "");
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(AI_SETTINGS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ restaurantId, provider, model, base_url: baseUrl, api_key: apiKey }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      apply(data);
      setSuccess(provider ? "Saved. The test request to the provider worked." : "Saved. Summaries now use the TableMate default.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-card">
      <div className="settings-card-header">
        <h2 className="settings-card-title">AI Summaries</h2>
      </div>

      <p className="settings-main-label">
        Choose which AI writes your chart summaries. With the TableMate default, no key is needed. To use your own
        account, pick a provider and model and paste your API key; it is stored encrypted and checked with a short
        test request when you save.
      </p>

      {!settings ? (
        <div className="settings-field">
          <span className="settings-label">{error ? "Error" : "AI"}</span>
          <span className={`settings-value${error ? " settings-error" : ""}`}>{error || "Loading..."}</span>
        </div>
      ) : (
        <form onSubmit={save} autoComplete="off">
          <div className="settings-field settings-backup-row">
            <span className="settings-label">Provider</span>
            <select className="settings-select" value={provider} onChange={(e) => changeProvider(e.target.value)}>
              <option value="">TableMate default</option>
              {Object.entries(providers).map(([value, p]) => (
                <option key={value} value={value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {provider && (
            <>
              <div className="settings-field settings-backup-row">
                <span className="settings-label">Model</span>
                <input
                  className="settings-input"
                  type="text"
                  list={`ai-models-${provider}`}
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder={info?.models?.length ? "Pick a suggestion or type any model" : "Model id from your provider"}
                  required
                />
                <datalist id={`ai-models-${provider}`}>
                  {(info?.models || []).map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>

              {info?.base_url && (
                <div className="settings-field settings-backup-row">
                  <span className="settings-label">API base URL</span>
                  <input
                    className="settings-input"
                    type="url"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                    placeholder={info.default_base_url}
                  />
                </div>
              )}

              <div className="settings-field settings-backup-row">
                <span className="settings-label">API key</span>
                <input
                  className="settings-input"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={keyKept ? `Saved (ends in ${settings.key_last4}). Type a new key to replace it.` : "Paste your API key"}
                  autoComplete="new-password"
                  required={!keyKept}
                />
              </div>
            </>
          )}

          {error && (
            <div className="settings-field">
              <span className="settings-label">Error</span>
              <span className="settings-value settings-error">{error}</span>
            </div>
          )}

          {success && (
            <div className="settings-field">
              <span className="settings-label">Status</span>
              <span className="settings-value settings-success">{success}</span>
            </div>
          )}

          <button className="tm-btn tm-btn-primary tm-btn-full" type="submit" disabled={saving}>
            {saving ? "Testing and saving..." : "Save AI Settings"}
          </button>
        </form>
      )}
    </section>
  );
}
